import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import api from '../api';
import { showError, showSuccess } from '../utils/errorHandler';

function AdminCancellations() {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    fetchCancellationRequests();
  }, []);

  const fetchCancellationRequests = async () => {
    try {
      const response = await api.get('/api/loans');
      // Filter loans with pending cancellation requests
      const pendingCancellations = response.data.filter(loan => 
        loan.cancellation_request && loan.cancellation_request.status === 'pending'
      );
      
      // Fetch lender names for loans that don't have them
      const loansWithNames = await Promise.all(
        pendingCancellations.map(async (loan) => {
          if (!loan.lender_name && loan.lender_id) {
            try {
              // Fetch lender info from users endpoint
              const lenderRes = await api.get(`/api/admin/lenders`);
              const lender = lenderRes.data.find(l => l.user._id === loan.lender_id);
              if (lender) {
                loan.lender_name = `${lender.user.name} ${lender.user.surname}`;
              }
            } catch (err) {
              console.error('Error fetching lender name:', err);
              loan.lender_name = 'Prestamista';
            }
          }
          return loan;
        })
      );
      
      setLoans(loansWithNames);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveCancellation = async (loanId, lenderName) => {
    if (!confirm(t('cancellations.confirmApprove', { name: lenderName }))) {
      return;
    }

    setProcessing(loanId);
    try {
      await api.post(`/api/admin/loans/${loanId}/approve-cancellation`);
      showSuccess(t('cancellations.approved'));
      fetchCancellationRequests();
    } catch (error) {
      showError(error, 'Error en aprovar sol·licitud');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectCancellation = async (loanId, lenderName) => {
    const reason = prompt(`Rebutjar la cancel·lació de ${lenderName}?\n\nMotiu (opcional):`);
    if (reason === null) return;

    setProcessing(loanId);
    try {
      await api.post(`/api/admin/loans/${loanId}/reject-cancellation`, {
        reason: reason || 'Sense motiu especificat'
      });
      showSuccess(t('cancellations.rejected'));
      fetchCancellationRequests();
    } catch (error) {
      showError(error, t('cancellations.errorReject'));
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="main-layout">
        <Sidebar />
        <div className="content-area" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-layout">
      <Sidebar />
      <div className="content-area">
        <TopBar title={`📋 ${t('admin.cancellations')}`} />

        <main style={{ padding: '2.5rem' }} className="fade-in">
          {loans.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '4rem', 
              background: 'rgba(255,255,255,0.03)', 
              borderRadius: '20px' 
            }}>
              <p style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</p>
              <h3 style={{ fontSize: '1.25rem', color: 'rgba(255,255,255,0.9)' }}>
                {t('admin.noCancellationRequests')}
              </h3>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1.5rem' }}>
              {loans.map((loan) => {
                const request = loan.cancellation_request;
                return (
                  <div 
                    key={loan._id} 
                    className="dark-card" 
                    style={{ 
                      padding: '2rem',
                      border: '1px solid rgba(249,115,22,0.3)',
                      background: 'rgba(249,115,22,0.05)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                      <div>
                        <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: 'white' }}>
                          {loan.lender_name || 'Prestamista'}
                        </h3>
                        <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.5)' }}>
                          Sol·licitat: {new Date(request.requested_at).toLocaleDateString('ca-ES', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <span style={{
                        padding: '0.5rem 1rem',
                        background: 'rgba(249,115,22,0.2)',
                        color: '#fb923c',
                        borderRadius: '20px',
                        fontSize: '0.8125rem',
                        fontWeight: '600',
                        height: 'fit-content'
                      }}>
                        ⏳ Pendiente
                      </span>
                    </div>

                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                      gap: '1.5rem', 
                      marginBottom: '1.5rem' 
                    }}>
                      <div style={{ padding: '1.5rem', background: 'rgba(59,130,246,0.1)', borderRadius: '12px' }}>
                        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>
                          CAPITAL
                        </p>
                        <p style={{ fontSize: '2rem', fontWeight: '700', color: '#60a5fa' }}>
                          {loan.capital.toLocaleString('es-ES')}€
                        </p>
                      </div>
                      <div style={{ padding: '1.5rem', background: 'rgba(16,185,129,0.1)', borderRadius: '12px' }}>
                        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>
                          {t('admin.currentInterest').toUpperCase()}
                        </p>
                        <p style={{ fontSize: '2rem', fontWeight: '700', color: '#34d399' }}>
                          {loan.interest_rate}%
                        </p>
                      </div>
                      <div style={{ padding: '1.5rem', background: 'rgba(239,68,68,0.1)', borderRadius: '12px' }}>
                        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>
                          {t('admin.newInterestWithPenalty').toUpperCase()}
                        </p>
                        <p style={{ fontSize: '2rem', fontWeight: '700', color: '#ef4444' }}>
                          {(loan.interest_rate - 2.5).toFixed(1)}%
                        </p>
                      </div>
                      <div style={{ padding: '1.5rem', background: 'rgba(249,115,22,0.1)', borderRadius: '12px' }}>
                        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>
                          {t('loanDetail.remainingDays').toUpperCase()}
                        </p>
                        <p style={{ fontSize: '2rem', fontWeight: '700', color: '#fb923c' }}>
                          {loan.days_remaining || 0}
                        </p>
                      </div>
                    </div>

                    {request.notes && (
                      <div style={{ 
                        padding: '1rem', 
                        background: 'rgba(255,255,255,0.03)', 
                        borderRadius: '8px', 
                        marginBottom: '1.5rem',
                        borderLeft: '3px solid #fb923c'
                      }}>
                        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.5rem' }}>
                          {t('admin.lenderNotes').toUpperCase()}:
                        </p>
                        <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.8)' }}>
                          {request.notes}
                        </p>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <button
                        onClick={() => handleApproveCancellation(loan._id, loan.lender_name || 'Prestamista')}
                        disabled={processing === loan._id}
                        style={{ 
                          flex: 1, 
                          padding: '1rem', 
                          background: 'linear-gradient(135deg, #007a33 0%, #00a843 100%)', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: '12px', 
                          fontWeight: '600', 
                          cursor: processing === loan._id ? 'not-allowed' : 'pointer',
                          opacity: processing === loan._id ? 0.5 : 1
                        }}
                      >
                        {processing === loan._id ? '⏳ Processant...' : `✓ Aprovar Sol·licitud`}
                      </button>
                      <button
                        onClick={() => handleRejectCancellation(loan._id, loan.lender_name || 'Prestamista')}
                        disabled={processing === loan._id}
                        style={{ 
                          padding: '1rem 2rem', 
                          background: 'rgba(239,68,68,0.1)', 
                          border: '1px solid rgba(239,68,68,0.3)', 
                          color: '#ef4444', 
                          borderRadius: '12px', 
                          fontWeight: '600', 
                          cursor: processing === loan._id ? 'not-allowed' : 'pointer',
                          opacity: processing === loan._id ? 0.5 : 1
                        }}
                      >
                        ✕ {t('admin.reject')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default AdminCancellations;
